import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Countdown } from '@pacer/shared';
import * as api from '../api';
import { Icon, type IconName } from './Icon';
import { PlanDetail } from './PlanDetail';
import { Card } from './ui';
import { daysUntil } from '../lib/countdowns';
import { useZone } from '../lib/dates';
import { queryKeys } from '../state/queries';

/**
 * The pinned plans, featured across the top of the dashboard: a big count of days left and a
 * prep progress bar. Tap one to open its checklist. Past plans drop off on their own.
 */
export function PinnedPlans() {
  const zone = useZone();
  const [open, setOpen] = useState<Countdown | null>(null);
  const { data: countdowns = [] } = useQuery({ queryKey: queryKeys.countdowns, queryFn: api.getCountdowns });

  const plans = countdowns.filter((c) => c.pinned && daysUntil(c.targetDate, zone) >= 0);
  if (plans.length === 0) return null;

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} zone={zone} onOpen={() => setOpen(plan)} />
        ))}
      </div>
      {open && <PlanDetail countdown={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function PlanCard({ plan, zone, onOpen }: { plan: Countdown; zone: string; onOpen: () => void }) {
  const days = daysUntil(plan.targetDate, zone);
  const color = plan.color ?? 'var(--color-accent)';
  const pct = plan.tasksTotal > 0 ? Math.round((plan.tasksDone / plan.tasksTotal) * 100) : 0;

  return (
    <button onClick={onOpen} className="text-left">
      <Card interactive className="flex h-full flex-col gap-3 border-l-2 p-4" style={{ borderLeftColor: color }}>
        <div className="flex items-center gap-3">
          <span
            className="flex size-11 shrink-0 items-center justify-center rounded-full"
            style={{ background: `color-mix(in srgb, ${color} 16%, transparent)`, color }}
          >
            <Icon name={(plan.icon as IconName) ?? 'star'} size={22} strokeWidth={1.3} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-display text-[18px] leading-tight text-primary">{plan.title}</div>
            <div className="mt-0.5 font-mono text-[12px] text-muted">
              {days === 0 ? 'today' : days === 1 ? 'tomorrow' : `${days} days away`}
            </div>
          </div>
          <div className="tabular text-right font-mono leading-none font-medium text-primary" style={{ color }}>
            <span className="text-[30px]">{days}</span>
            <div className="mt-0.5 font-mono text-[11px] tracking-wide text-muted uppercase">
              {days === 1 ? 'day' : 'days'}
            </div>
          </div>
        </div>

        {plan.tasksTotal > 0 && (
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border-subtle">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="tabular font-mono text-[12px] text-muted">
              {plan.tasksDone}/{plan.tasksTotal} done
            </span>
          </div>
        )}
      </Card>
    </button>
  );
}
